import React from 'react';
import styled from 'styled-components';
import {
  Box, Text, TextInput, Button, RangeSelector,
} from 'grommet';
import { ReactSVG } from 'react-svg';
import DropButton from '../../Control/Buttons/DropButton';

const StyledText = styled(Text)`
  line-height: 1.25;
`;

const StyledTextInput = styled(TextInput)`
  background: var(--page-input-bg);
  font-size: 0.8rem;
  border: solid 1px var(--page-input-border);
  padding: 0.3rem;
  border-radius: var(--app-radius);
  height: var(--table-controls-height);
  width: 12rem;

  ${({ withIcon }) => withIcon && `
    padding-left: 1.75rem;
  `}

  &::placeholder {
    color: var(--page-input-text);
  }

  &:focus {
    border-color: ${({ focusHighlight }) => focusHighlight};
  }

  @media (max-width: 768px) {
    width: 14.5rem;

    ${({ withIcon }) => withIcon && `
      padding-left: 2.2rem;
    `};
  }
`;

const StyledRaisedContainer = styled(Box)`
  border-radius: ${({ noRound }) => (noRound ? '0' : 'var(--app-radius)')};
  box-shadow: ${({ noShadow }) => (noShadow ? 'none' : 'var(--elevation)')};
  background-color: ${({ background }) => background || 'var(--page-alt-bg)'};
`;

const StyledSVG = styled(ReactSVG)`
  svg {
    height: ${({ height }) => height};
    width:${({ width }) => width};

    ${({ $fillColor }) => $fillColor && `
      fill: ${$fillColor};

      path, rect {
        fill: ${$fillColor};
      }
    `};

    &:hover {
      fill: ${({ $hoverColor }) => $hoverColor};

      path, rect {
        fill: ${({ $hoverColor }) => $hoverColor};
      }
    }
  }
`;

const StyledHoverContainer = styled(Box)`
  &:hover {
    svg > path {
      fill: ${({ hoverColor }) => hoverColor};
    }
  }
`;

const StyledSimpleDrop = styled((props) => <DropButton {...props} />)`
  border-radius: var(--app-radius);

  > button {
    background: var(--page-input-bg);
    font-size: 0.8rem;
    padding: 0.3rem;
    border-radius: var(--app-radius);
    border: ${({ isOpen, highlight }) => (isOpen ? `1px solid ${highlight}` : '1px solid var(--page-input-border)')};
    width: ${({ larger }) => (larger ? '4.5rem' : '3.15rem')};
    height: ${({ larger }) => (larger ? '1.85rem' : '1.6rem')};

    &:focus {
      border-color: ${({ highlight }) => highlight};
    }

    > div {
      border-radius: var(--app-radius);
      width: 100%;
      -webkit-box-pack: justify;
      -ms-flex-pack: justify;
      justify-content: center;
    }
  }

  ${({ larger }) => larger && `
    svg {
      padding-top: 0.25rem;
    }
  `}
`;

const StyledFlexibleDrop = styled((props) => <DropButton {...props} />)`
  border-radius: var(--app-radius);

  > button {
    background: ${({ bgColor }) => bgColor ?? 'var(--page-input-bg)'};
    font-size: 0.8rem;
    padding: ${({ customPad }) => customPad || '0.3rem'};
    border-radius: var(--app-radius);
    border: ${({ noBorder }) => !noBorder && '1px solid var(--page-input-border)'};
    border-color: ${({ isOpen, highlight }) => (isOpen ? highlight : 'var(--page-input-border)')};
    min-width: ${({ width }) => width};
    min-height: ${({ height }) => height};

    &:focus {
      border-color: ${({ highlight }) => highlight};
    }

    > div {
      border-radius: var(--app-radius);
      width: 100%;
      -webkit-box-pack: justify;
      -ms-flex-pack: justify;
      justify-content: center;
    }
  }
`;

const StyledPaginationArrow = styled((props) => <Button {...props} />)`
  border-radius: var(--app-radius);
  border: 1px solid var(--page-input-border);
  background: var(--page-input-bg);
  padding: 0.15rem;
  width: 1.6rem;
  height: 1.6rem;
  display: flex;
  justify-content: center;
  align-items: center;

  &:focus {
    border: ${({ highlight }) => `1px solid ${highlight}`};
  }
`;

const StyledSelectMenu = styled(Button)`
  padding: 0.5rem;
  border-radius: 6px;

  &:hover {
    color: ${({ highlightText }) => highlightText};
  }
`;

const StyledSearchInputClear = styled(Button)`
  position: absolute;
  padding-right: 0.5rem;
  top: 0;
  bottom: 0;
  right: 0;
`;

const StyledRangeSelect = styled(RangeSelector)`
  span {
    width: 3rem;
    min-width: 3rem;
  }
`;

const StyledIconButton = styled(Button)`
  border-radius: var(--app-radius);
  padding: 0.25rem 0.6rem;
  background: ${({ bgColor }) => bgColor ?? 'var(--page-input-bg)'};
  font-weight: 600;
  margin: ${({ noMargin }) => (noMargin ? 'none' : '0.25rem')};
  height: 2rem;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const StyledSeeDetailsButton = styled(Button)`
  color: ${({ textColor }) => textColor};
  min-width: 7rem;

  &:hover {
    color: ${({ hoverTextColor }) => hoverTextColor};

    div > span {
      color: ${({ hoverTextColor }) => hoverTextColor};
    }

    svg {
      fill: ${({ hoverTextColor }) => hoverTextColor};

      path, rect {
        fill: ${({ hoverTextColor }) => hoverTextColor};
      }
    }
  }
`;

export {
  StyledText,
  StyledTextInput,
  StyledRaisedContainer,
  StyledSVG,
  StyledHoverContainer,
  StyledSimpleDrop,
  StyledFlexibleDrop,
  StyledSelectMenu,
  StyledPaginationArrow,
  StyledSearchInputClear,
  StyledRangeSelect,
  StyledIconButton,
  StyledSeeDetailsButton,
};
